import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Dialog, withStyles, Tooltip } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import { Storage } from 'aws-amplify';

import Constant from '@/common/constant/Constant';
import DownloadFileIcon from '@/component/common/icons/DownloadFileIcon';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';

ViewImage.propTypes = {
    imageData: PropTypes.object
};

function ViewImage({ imageData }) {
    useTranslationSubscription();
    const [openPreview, setOpenPreview] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [isImageError, setIsImageError] = useState(false);
    const [isShowFlagMessage, setIsShowFlagMessage] = useState(false);

    const handleImageLoad = () => setIsImageLoaded(true);
    const handleImageError = () => setIsImageError(true);

    const FlagMessageCopyLink = withStyles({
        tooltip: {
            color: '#1DA578',
            backgroundColor: '#D7F4E2',
            padding: '8px 16px',
            fontSize: '14px',
            boxShadow: '6px 5px 5px -4px rgba(0,0,0,0.3)'
        },
        arrow: {
            color: '#D7F4E2'
        }
    })(Tooltip);

    const downloadImage = useCallback(async () => {
        const imageUrl = imageData?.localUrl;

        try {
            const response = await fetch(imageUrl, { mode: 'cors' });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // Get original filename from imageData or extract from s3Path/url
            let fileName = imageData?.fileName;
            if (!fileName && imageData?.s3Path) {
                // Extract filename from s3Path (e.g., "public/.../filename.jpg")
                fileName = imageData.s3Path.split('/').pop();
                // Pattern: {originalName}_{uuid}.{ext} - try to extract originalName
                const uuidPattern = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\.[^.]+)$/i;
                if (uuidPattern.test(fileName)) {
                    fileName = fileName.replace(uuidPattern, '$1');
                }
            }
            if (!fileName) {
                // Fallback: extract from URL or generate random name
                const urlPath = imageUrl.split("?")[0];
                const extMatch = urlPath.match(/\.[a-zA-Z0-9]+$/);
                const ext = extMatch ? extMatch[0] : ".jpg";
                const randomName = Math.random().toString(36).substring(2, 10);
                fileName = `${randomName}${ext}`;
            }
            link.setAttribute('download', fileName);

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('downloadImage error :', error);
        }
    }, [imageData]);

    const copyLinkImage = useCallback(async () => {
        try {
            let localUrl = imageData.localUrl;
            if (localUrl.startsWith("blob")) {
                let s3Path = imageData?.s3Path || '';
                if (s3Path.startsWith('public/')) {
                    s3Path = s3Path.slice(7);
                }
                localUrl = await Storage.get(s3Path, { 
                    level: 'public',
                    expires: Constant.AI_PRESIGNED_URL_EXPIRE_TIME
                });
            }
            await navigator.clipboard.writeText(localUrl);

            setIsShowFlagMessage(true);

            setTimeout(() => {
                setIsShowFlagMessage(false);
            }, 1000);

        } catch (err) {
            console.log('copyLinkImage err :', err);
        }
    }, [imageData]);

    if (!imageData?.localUrl) {
        return (
            <div
                className="ai-image-skeleton"
                style={{
                    minWidth: 145,
                    minHeight: 145,
                    borderRadius: 8,
                    overflow: "hidden",
                    position: "relative"
                }}
            >
                <div className="shimmer"></div>
            </div>
        );
    }

    return (
        <>
            {isImageError ? (
                <div className="ai-chat-msg-attachment-img-wrapper">
                    <img
                        className="ai-chat-view-image"
                        alt="preview-img-error"
                        src={imageData.localUrl}
                    />
                </div>
            ) : (
                <div className="ai-chat-msg-attachment-img-wrapper">
                    <button
                        className="btn-reset-default ai-chat-msg-attachment-img"
                        onClick={() => setOpenPreview(true)}
                    >
                        <img
                            className="ai-chat-view-image"
                            alt="preview-img"
                            src={imageData.localUrl}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            style={{
                                opacity: isImageLoaded ? 1 : 0.5,
                                filter: isImageLoaded ? 'none' : 'blur(2px)'
                            }}
                        />
                    </button>
                    <div className="ai-chat-view-image-options">
                        <Tooltip title={trI18n('DOWNLOAD')} placement="top" arrow>
                            <button
                                className="btn-reset-default image-option-item"
                                onClick={downloadImage}
                            >
                                <DownloadFileIcon />
                            </button>
                        </Tooltip>
                        <Tooltip title={trI18n('COPY_URL')} placement="top" arrow>
                            <button
                                className="btn-reset-default image-option-item"
                                onClick={copyLinkImage}
                            >
                                <FlagMessageCopyLink
                                    title={trI18n('KNOWDEMSG002')}
                                    placement="top-start"
                                    arrow={true}
                                    open={isShowFlagMessage}
                                >
                                    <LinkIcon className="ai-img-copy-link-icon" />
                                </FlagMessageCopyLink>
                            </button>
                        </Tooltip>
                    </div>
                </div>
            )}

            <Dialog open={openPreview} onClose={() => setOpenPreview(false)} maxWidth="md">
                <img
                    alt="preview-full"
                    src={imageData.localUrl}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        objectFit: 'contain'
                    }}
                />
            </Dialog>
        </>
    );
}

export default ViewImage;
